import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Home, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const MortgageCalculatorTool = () => {
  const [homePrice, setHomePrice] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [downPaymentType, setDownPaymentType] = useState('percent');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('30');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showAmortization, setShowAmortization] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const calculate = () => {
    const price = parseFloat(homePrice);
    const rate = parseFloat(interestRate);
    const years = parseFloat(loanTerm);

    if (isNaN(price) || isNaN(rate) || isNaN(years) || price <= 0 || years <= 0) {
      setResult(null);
      return;
    }

    // Calculate down payment amount
    let downPaymentAmount;
    if (downPaymentType === 'percent') {
      const downPercent = parseFloat(downPayment) || 0;
      downPaymentAmount = (downPercent / 100) * price;
    } else {
      downPaymentAmount = parseFloat(downPayment) || 0;
    }

    // Calculate loan amount (principal)
    const principal = price - downPaymentAmount;

    if (principal <= 0) {
      setResult(null);
      return;
    }

    // Standard mortgage amortization formula
    // M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const monthlyRate = rate / 100 / 12;
    const totalPayments = years * 12;

    let monthlyPayment;
    if (monthlyRate === 0) {
      monthlyPayment = principal / totalPayments;
    } else {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
    }

    const totalPayment = monthlyPayment * totalPayments;
    const totalInterest = totalPayment - principal;
    const downPaymentPercent = (downPaymentAmount / price) * 100;

    // Generate amortization schedule summary (yearly)
    const amortizationSchedule = [];
    let remainingBalance = principal;
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;

    for (let month = 1; month <= totalPayments; month++) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      remainingBalance -= principalPayment;

      yearlyPrincipal += principalPayment;
      yearlyInterest += interestPayment;

      if (month % 12 === 0 || month === totalPayments) {
        amortizationSchedule.push({
          year: Math.ceil(month / 12),
          principalPaid: yearlyPrincipal,
          interestPaid: yearlyInterest,
          totalPaid: yearlyPrincipal + yearlyInterest,
          remainingBalance: Math.max(0, remainingBalance)
        });
        yearlyPrincipal = 0;
        yearlyInterest = 0;
      }
    }

    setResult({
      monthlyPayment,
      totalPayment,
      totalInterest,
      principal,
      homePrice: price,
      downPaymentAmount,
      downPaymentPercent,
      interestRate: rate,
      years,
      totalPayments,
      amortizationSchedule
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setHomePrice('');
    setDownPayment('');
    setInterestRate('');
    setLoanTerm('30');
    setResult(null);
    setShowAmortization(false);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const faqs = [
    {
      question: "What is a mortgage?",
      answer: "A mortgage is a loan used to purchase real estate, where the property serves as collateral. You repay the loan over time with interest, typically in monthly payments over 15-30 years."
    },
    {
      question: "What's included in a mortgage payment?",
      answer: "A typical mortgage payment includes Principal (loan amount), Interest, Taxes, and Insurance (PITI). This calculator shows principal and interest only. Add 1-3% of home value annually for taxes and insurance."
    },
    {
      question: "How does the interest rate affect my payment?",
      answer: "Higher interest rates increase your monthly payment and total interest paid. A 1% rate increase on a $400,000 loan can add $200+ to your monthly payment and $70,000+ in total interest over 30 years."
    },
    {
      question: "Should I choose a 15-year or 30-year mortgage?",
      answer: "A 15-year mortgage has higher monthly payments but saves significant interest (often 50%+ less). A 30-year mortgage offers lower payments but costs more overall. Choose based on your budget and goals."
    },
    {
      question: "What is PMI and how do I avoid it?",
      answer: "Private Mortgage Insurance (PMI) is required when your down payment is less than 20%. It typically costs 0.5-1% of the loan annually. Put 20% down or wait until you have 20% equity to eliminate PMI."
    },
    {
      question: "How can I get a better interest rate?",
      answer: "Improve your credit score (740+ gets best rates), save for a larger down payment, shop multiple lenders, consider paying points upfront, and lock your rate when market conditions are favorable."
    },
    {
      question: "What is amortization?",
      answer: "Amortization is how your loan is paid off over time. Early payments are mostly interest; later payments are mostly principal. The amortization schedule shows this breakdown for each period."
    },
    {
      question: "Can I pay off my mortgage early?",
      answer: "Yes! Extra payments go toward principal, reducing total interest. Even small additional payments can save thousands and shorten your loan term. Check for prepayment penalties first."
    }
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
          <Home size={16} />
          Property Details
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Home Price ($)
            </label>
            <input
              type="number"
              value={homePrice}
              onChange={(e) => setHomePrice(e.target.value)}
              placeholder="e.g., 450000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="home-price-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Down Payment
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                placeholder={downPaymentType === 'percent' ? 'e.g., 20' : 'e.g., 90000'}
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                data-testid="down-payment-input"
              />
              <select
                value={downPaymentType}
                onChange={(e) => setDownPaymentType(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                data-testid="down-payment-type"
              >
                <option value="percent">%</option>
                <option value="amount">$</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 6.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Loan Term
            </label>
            <select
              value={loanTerm}
              onChange={(e) => setLoanTerm(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              data-testid="loan-term-select"
            >
              <option value="30">30 years</option>
              <option value="25">25 years</option>
              <option value="20">20 years</option>
              <option value="15">15 years</option>
              <option value="10">10 years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-mortgage-btn"
      >
        <Calculator size={18} />
        Calculate Mortgage
      </button>

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="mortgage-results">
          {/* Main Results */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Monthly Payment</h3>
            <div className="flex items-center justify-between">
              <div className="text-4xl font-bold text-green-400" data-testid="monthly-payment-result">
                {formatCurrency(result.monthlyPayment)}
              </div>
              <button
                onClick={() => copyResult(result.monthlyPayment, 'monthly')}
                className={`p-2 rounded transition-colors ${
                  copied === 'monthly' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-monthly-payment"
              >
                <Copy size={18} />
              </button>
            </div>
            <div className="text-sm text-gray-400 mt-2">
              Principal & Interest only (excludes taxes, insurance, HOA)
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Payment Breakdown</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-400">Total Interest</span>
                  <button
                    onClick={() => copyResult(result.totalInterest, 'interest')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'interest' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <Copy size={12} />
                  </button>
                </div>
                <div className="text-xl font-bold text-yellow-400" data-testid="total-interest-result">
                  {formatCurrency(result.totalInterest)}
                </div>
              </div>
              <div className="p-3 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-400">Total Payment</span>
                  <button
                    onClick={() => copyResult(result.totalPayment, 'total')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'total' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <Copy size={12} />
                  </button>
                </div>
                <div className="text-xl font-bold text-blue-400" data-testid="total-payment-result">
                  {formatCurrency(result.totalPayment)}
                </div>
              </div>
            </div>
          </div>

          {/* Loan Summary */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Loan Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Home Price</span>
                <span className="text-white font-medium">{formatCurrency(result.homePrice)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Down Payment</span>
                <span className="text-white font-medium">
                  {formatCurrency(result.downPaymentAmount)} ({result.downPaymentPercent.toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Loan Amount</span>
                <span className="text-white font-medium">{formatCurrency(result.principal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Rate</span>
                <span className="text-white font-medium">{result.interestRate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Loan Term</span>
                <span className="text-white font-medium">{result.years} years ({result.totalPayments} payments)</span>
              </div>
            </div>
          </div>

          {/* Amortization Summary */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <button
              onClick={() => setShowAmortization(!showAmortization)}
              className="w-full flex items-center justify-between text-sm font-medium text-gray-300"
              data-testid="toggle-amortization"
            >
              <span>Amortization Schedule (Yearly Summary)</span>
              {showAmortization ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            
            {showAmortization && (
              <div className="mt-4 overflow-x-auto" data-testid="amortization-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-700">
                      <th className="text-left py-2 pr-4">Year</th>
                      <th className="text-right py-2 px-2">Principal</th>
                      <th className="text-right py-2 px-2">Interest</th>
                      <th className="text-right py-2 px-2">Total Paid</th>
                      <th className="text-right py-2 pl-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.amortizationSchedule.map((row) => (
                      <tr key={row.year} className="border-b border-gray-700/50">
                        <td className="py-2 pr-4 text-white">{row.year}</td>
                        <td className="py-2 px-2 text-right text-green-400">{formatCurrency(row.principalPaid)}</td>
                        <td className="py-2 px-2 text-right text-yellow-400">{formatCurrency(row.interestPaid)}</td>
                        <td className="py-2 px-2 text-right text-blue-400">{formatCurrency(row.totalPaid)}</td>
                        <td className="py-2 pl-2 text-right text-gray-300">{formatCurrency(row.remainingBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
          <HelpCircle size={16} />
          Frequently Asked Questions
        </h3>
        <div className="space-y-2">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                className="w-full flex items-center justify-between p-3 text-left text-sm text-gray-300 hover:bg-gray-700/50 transition-colors"
                data-testid={`faq-${index}`}
              >
                <span>{faq.question}</span>
                {expandedFaq === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedFaq === index && (
                <div className="px-3 pb-3 text-sm text-gray-400">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Current Mortgage Rates (Typical)</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">30-Year Fixed</div>
            <div>6.5% - 7.5% APR</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">15-Year Fixed</div>
            <div>5.75% - 6.75% APR</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">5/1 ARM</div>
            <div>5.5% - 6.5% APR</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Down Payment Guidelines</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• <span className="text-white">20%</span> - Avoid PMI (Private Mortgage Insurance)</div>
          <div>• <span className="text-white">10-19%</span> - Lower PMI, good for most buyers</div>
          <div>• <span className="text-white">3-5%</span> - Minimum for conventional loans (higher PMI)</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-mortgage-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default MortgageCalculatorTool;
